import { S3Client } from '@aws-sdk/client-s3';
import type OSS from 'ali-oss';
import COS from 'cos-js-sdk-v5';

import { AwsUploader } from '../Uploader/CommonUploader/Uploader/AwsUploader';
import { OssUploader } from '../Uploader/CommonUploader/Uploader/OssUploader';
import { CosUploader } from './CommonUploader/Uploader/CosUploader';

export const enum TaskStatus {
  SUCCESS = 'success',
  UPLOADING = 'uploading',
  FAILED = 'failed',
  TRANSCODING = 'transcoding',
  TRANSCODING_FAILED = 'transcoding_failed',
}

export interface IFile {
  id: string;
  file: File;
  // 是否需要转码服务
  needTransCode?: boolean;
  // 是否转码后转存
  needTransfer?: boolean;
  dirSceneType?: number;
}

export interface IUploadOptions {
  workspace: string;
  files: IFile[];
  dirSceneType: number;
  // 是否需要转码服务
  // needTransCode?: boolean;
  options?: {
    parallel?: number;
    partSize?: number;
    [propname: string]: any;
  };
}

export interface IUploadDBItem {
  workspace: string;
  value: {
    [propname: string]: {
      workspace: string;
      file: File;
    };
  };
}

export interface IReuploadInfo {
  file: File;
  dirSceneType: number;
  accessUrl: string;
  filePath: string;
  checkPoint?: OSS.Checkpoint;
  uploadId: string;
  tokenData: any;
  retryInfo?: {
    tokenData: any;
    fileName: string;
    dirSceneType: number;
  };
  client?: OSS | S3Client;
}

export interface IUpload {
  file: File;
  fileUid?: string;
  tokenData: IRequestTokenData;
}

export interface IUploaderProps {
  file: File;
  fileUid?: string;
  tokenData: ITokenData;
}

export interface IUploadInstanceOptions {
  onBefore?: onBefore;
  onProgress?: onProgress;
  onTranscode?: onTranscode;
  onSuccess?: onSuccess;
  onCancel?: onCancel;
  onError?: onError;
  onStart?: onStart;
  // 毫秒
  timeout?: number;
}

export enum ErrorType {
  Other = 'Other',
  // 请求链接超时
  ConnectionTimeoutError = 'ConnectionTimeoutError',
  // 请求超时
  RequestTimeoutError = 'RequestTimeoutError',
  //网络中断/异常
  RequestError = 'RequestError',
  // 签名错误
  SignatureDoesNotMatchError = 'SignatureDoesNotMatchError',
  // 服务器时间与本机不匹配
  RequestTimeTooSkewedError = 'RequestTimeTooSkewedError',
  //分片ID不存在
  NoSuchUploadError = 'NoSuchUploadError',
  // OSS内存异常
  InternalError = 'InternalError',
  // 无效分片
  InvalidPartError = 'InvalidPartError',
  // 拒绝访问
  AccessDeniedError = 'AccessDeniedError',
  // 视频转码失败
  TranscodingError = 'TranscodingError',
}

interface IErrorInfo {
  name: string;
  message: string;
  error: Error;
  start: number;
  end: number;
}

interface ICompleteInfo {
  start: number;
  end: number;
}

type onBefore = (options: { file: File }) => void;
type onProgress = (data: { progress: number; status: boolean; url: string }) => void;
type onTranscode = (data: { url: string }) => void;
type onSuccess = (url: string, completeInfo: ICompleteInfo) => void;
type onCancel = (fileId: string) => void;
type onError = (
  error: IErrorInfo,
  fileId: string,
  helper?: Partial<AwsUploader | OssUploader | CosUploader>,
) => void;

type onStart = (startTime: number) => void;

export enum UploadType {
  OSS = 'ali',
  AWS = 'aws',
  COS = 'cos',
}

// Token接口返回的参数
export interface ITokenData {
  accessKey: string;
  accessSecret: string;
  securityToken: string;
  bucket: string;
  accessUrl: string;
  coverUrl?: string;
  region: string;
  filePath: string;
  ossType: UploadType;
  uploadHost: string;
}

// 上传进度中的参数
export interface IProgressData {
  percent?: number;
  checkPoint?: OSS.Checkpoint;
  status?: TaskStatus;
  isStart?: boolean;
  isLoggedSent?: boolean;
  needTransCode?: boolean;
  needTransfer?: boolean;
  transCodeHandler?: () => void;
}

// 请求Token接口时的参数
export interface IRequestTokenData {
  fileName: string;
  dirSceneType: number;
}

// 存到LocalStorage中的参数
export interface IStorageItem {
  uploadId: string;
  requestTokenData: IRequestTokenData;
  progressData: IProgressData;
  tokenData: ITokenData;
  fileName: string;
  errorInfo?: IErrorInfo;
  completeInfo?: ICompleteInfo;
}

// 存到store中的参数
export interface IStoreItem {
  uploadId: string;
  requestTokenData: IRequestTokenData;
  progressData: IProgressData;
  tokenData: ITokenData;
  fileName: string;
  file: File;
  client: OSS | S3Client | COS | null;
  errorInfo?: IErrorInfo;
  completeInfo?: ICompleteInfo;
}
